<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { MAX_BOX_FOLDER_URLS } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import BoxFolderDeleteModal from '@/components/admin/user-documents/box-folder-delete-modal.vue';
import { BoxFolder } from '@/types';

const router = useRouter();

type Props = {
  initBoxFolders?: BoxFolder[];
  isSubmitting: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  initBoxFolders: () => [],
});

type Emits = {
  submit: [boxFolders: BoxFolder[]];
};
const emit = defineEmits<Emits>();

// アクションメニュー
const openActionMenu = (index: number) => {
  closeActionMenu();
  folderForms.value[index].isOpenActionMenu = true;
};
const closeActionMenu = () =>
  folderForms.value.forEach(form => (form.isOpenActionMenu = false));

// フォームの状態管理
const folderForms = ref<
  {
    folder: BoxFolder;
    isOpenEditForm: boolean;
    isOpenActionMenu: boolean;
    isOpenDeleteModal: boolean;
  }[]
>(
  props.initBoxFolders.map(folder => ({
    folder,
    isOpenEditForm: false,
    isOpenActionMenu: false,
    isOpenDeleteModal: false,
  })),
);
// 初期化
watch(
  () => props.initBoxFolders,
  initBoxFolders => {
    folderForms.value = initBoxFolders.map(folder => ({
      folder,
      isOpenEditForm: false,
      isOpenActionMenu: false,
      isOpenDeleteModal: false,
    }));
  },
);
const closeAllFolderForms = () => {
  folderForms.value.forEach(folderForm => (folderForm.isOpenEditForm = false));
};

// URLの編集フォーム
const editingFormValue = ref('');
const updateFolderForm = (index: number) => {
  folderForms.value[index].folder.folder_url = editingFormValue.value;
  folderForms.value[index].isOpenEditForm = false;
  editingFormValue.value = '';
};
const cancelEditForm = (index: number) => {
  folderForms.value[index].isOpenEditForm = false;
  editingFormValue.value = '';
};
const openEditForm = (index: number) => {
  closeAllFolderForms();
  folderForms.value[index].isOpenEditForm = true;
  editingFormValue.value = folderForms.value[index].folder.folder_url;
};
const isEditFormButtonDisabled = computed(
  () =>
    editingFormValue.value === '' ||
    folderForms.value.some(
      form => form.folder.folder_url === editingFormValue.value,
    ),
);

// URLの追加フォーム
const addFormValue = ref('');
const addFolderForm = () => {
  closeAllFolderForms();
  folderForms.value.push({
    folder: { id: null, folder_url: addFormValue.value },
    isOpenEditForm: false,
    isOpenActionMenu: false,
    isOpenDeleteModal: false,
  });
  addFormValue.value = '';
};
const isAddFormButtonDisabled = computed(
  () =>
    addFormValue.value === '' ||
    folderForms.value.some(
      form => form.folder.folder_url === addFormValue.value,
    ),
);
const isAddFormVisible = computed(
  () => folderForms.value.length < MAX_BOX_FOLDER_URLS,
);

// 削除モーダル
const openDeleteModal = (index: number) => {
  closeAllFolderForms();
  folderForms.value[index].isOpenDeleteModal = true;
};
const closeDeleteModal = () => {
  folderForms.value.forEach(form => (form.isOpenDeleteModal = false));
};
const deleteFolderForm = (index: number) => {
  folderForms.value.splice(index, 1);
  closeDeleteModal();
};

onMounted(() => {
  document.addEventListener('click', closeActionMenu);
});
onUnmounted(() => {
  document.removeEventListener('click', closeActionMenu);
});

const submit = () => {
  emit(
    'submit',
    folderForms.value.map(form => form.folder),
  );
};
const isSubmitButtonDisabled = computed(
  () => props.isSubmitting || folderForms.value.length === 0,
);
</script>

<template>
  <div class="box-folder-form">
    <div class="form-content">
      <div class="form-header">
        <div class="c-title-bold--m">認証するBOXフォルダ</div>
        <div class="c-text-regular--s form-count">
          ・{{ folderForms.length }}件/{{ MAX_BOX_FOLDER_URLS }}件
        </div>
      </div>
      <div class="notice-row c-text-regular--s">
        フォルダURLは正規化して保存されます
      </div>

      <div class="form-list">
        <div class="c-text-regular--s title">URL</div>
        <div class="c-text-regular--m">
          <div
            v-for="(folderForm, i) in folderForms"
            :key="i"
            class="form-item"
          >
            <div v-if="folderForm.isOpenEditForm" class="edit-url-form">
              <input
                type="text"
                class="c-textInput"
                v-model="editingFormValue"
                placeholder="https://"
              />
              <div class="form-buttons">
                <button
                  class="c-btn c-btn--auto c-btnOutline"
                  @click="cancelEditForm(i)"
                >
                  キャンセル
                </button>
                <button
                  class="c-btn c-btn--auto c-btn--AnewsPrimary"
                  :class="{ disabled: isEditFormButtonDisabled }"
                  :disabled="isEditFormButtonDisabled"
                  @click="updateFolderForm(i)"
                >
                  保存
                </button>
              </div>
            </div>
            <div v-else class="url-display">
              <div class="url-text">
                {{ folderForm.folder.folder_url }}
              </div>
              <div class="action">
                <div
                  class="action-icon"
                  data-testid="action-icon"
                  @click.stop="openActionMenu(i)"
                >
                  <DgrIcon name="ellipsis-h" size="small" />
                </div>
                <div class="action-menu" v-if="folderForm.isOpenActionMenu">
                  <div class="action-menu-item" @click="openEditForm(i)">
                    編集
                  </div>
                  <div class="action-menu-item" @click="openDeleteModal(i)">
                    削除
                  </div>
                </div>
              </div>
            </div>
            <BoxFolderDeleteModal
              v-if="folderForm.isOpenDeleteModal"
              v-model:open="folderForm.isOpenDeleteModal"
              :url="folderForm.folder.folder_url"
              @submit="deleteFolderForm(i)"
            />
          </div>
          <div class="add-url-form" v-if="isAddFormVisible">
            <input
              type="text"
              class="c-textInput"
              v-model="addFormValue"
              placeholder="https://"
            />
            <button
              class="c-btn c-btn--auto c-btn--AnewsPrimary"
              :class="{ disabled: isAddFormButtonDisabled }"
              :disabled="isAddFormButtonDisabled"
              @click="addFolderForm"
            >
              追加
            </button>
          </div>
        </div>
        <div class="buttons">
          <button class="c-btn c-btn--auto c-btnOutline" @click="router.back()">
            キャンセル
          </button>
          <button
            class="c-btn c-btn--auto c-btn--AnewsPrimary"
            :disabled="isSubmitButtonDisabled"
            :class="{ disabled: isSubmitButtonDisabled }"
            @click="submit"
          >
            認証
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.box-folder-form {
  display: flex;
  justify-content: center;
}
.form-content {
  flex-direction: column;
  min-width: 636px;
  margin: 24px 32px;
}
.form-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 4px;
  .form-count {
    color: $color-gray600;
  }
}
.notice-row {
  margin-bottom: 12px;
  color: $color-gray1000;
}
.form-list {
  background-color: $color-white;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  padding: 16px;
  .title {
    padding: 8px 0;
    border-bottom: 1px solid $color-gray400;
  }
  .buttons {
    display: flex;
    justify-content: flex-start;
    padding-top: 16px;
    gap: 8px;
  }
}
.form-item {
  border-bottom: 1px solid $color-gray400;
}
.url-display {
  padding: 8px;
  display: flex;
  justify-content: space-between;
  gap: 32px;
  align-items: center;
  .action-icon {
    display: flex;
    justify-content: center;
    padding: 8px;
    border-radius: 4px;
    &:hover {
      background-color: $color-gray200;
    }
  }
  .url-text {
    word-break: break-all;
  }
}
.edit-url-form {
  display: flex;
  padding: 8px 0;
  flex-direction: column;
  gap: 4px;
  .form-buttons {
    display: flex;
    gap: 4px;
  }
}
.add-url-form {
  display: flex;
  padding: 8px 0;
  gap: 4px;
  button {
    white-space: nowrap;
  }
}
.action {
  position: relative;
  .action-icon {
    cursor: pointer;
    border-radius: 4px;
    &:hover {
      background: $color-gray200;
    }
  }
  .action-menu {
    position: absolute;
    right: 0;
    width: 98px;
    background: $color-white;
    border: 1px solid $color-gray400;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 17px 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    .action-menu-item {
      padding: 0 12px;
      cursor: pointer;
      &:hover {
        background: $color-gray200;
      }
    }
  }
}
</style>
